<template>
  <div class="ux-menu" :class="{ right }">
    <span>
      <slot name="activator" :on="on" />
    </span>
    <teleport to="body">
      <transition name="ux-menu-transition" appear>
        <!-- @after-leave="isVisible = false" -->
        <div
          class="ux-menu-outer"
          v-if="isExpanded"
          v-show="isVisible"
          :style="outerStyle"
        >
          <div
            class="ux-menu-panel"
            :class="{ flex, overflow }"
            ref="menu"
            :style="innerStyle"
          >
            <slot />
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>
<script>
import UXMenuService from "./menu.service.js";

export default {
  props: {
    group: {
      type: String,
    },
    right: {
      type: Boolean,
      default: false,
    },
    bottom: {
      type: Boolean,
      default: false,
    },
    stop: {
      type: Boolean,
      default: false,
    },
    flex: {
      type: Boolean,
      default: false,
    },
    overflow: {
      type: Boolean,
    },
    closeOnClick: {
      type: Boolean,
      default: true,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      outerStyle: {},
      innerStyle: {},
      anchorRect: null,
      isExpanded: this.modelValue,
      isVisible: this.visible,
      triggerEvent: null,
      refMenu: null,
      mounted: false,
      repositionFrame: null,
      resizeObserver: null,
    };
  },
  watch: {
    modelValue(m) {
      this.expanded = m;
    },
    visible(m) {
      this.isVisible = m;
    },
  },
  mounted() {
    this.refMenu = this.$refs.menu;
    this.mounted = true;
  },
  beforeUnmount() {
    this.removeListeners();
    this.refMenu = null;
    this.mounted = false;

    if (UXMenuService.cache[this.group] === this) {
      UXMenuService.cache[this.group] = null;
    }
  },
  methods: {
    removeListeners() {
      document.removeEventListener("click", this.globalClick);
      window.removeEventListener("resize", this.scheduleReposition);
      window.removeEventListener("scroll", this.scheduleReposition, true);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.repositionFrame) {
        cancelAnimationFrame(this.repositionFrame);
        this.repositionFrame = null;
      }
      if (this.refMenu) {
        this.refMenu.removeEventListener("click", this.immediateClick);
      }
    },
    addEventListeners() {
      document.addEventListener("click", this.globalClick);
      window.addEventListener("resize", this.scheduleReposition);
      window.addEventListener("scroll", this.scheduleReposition, true);
      // Defer until the teleported panel has rendered, then watch it for
      // size changes (e.g. async content loading, nested expanders).
      this.$nextTick(() => {
        const menuEl = this.$refs.menu;
        if (
          !menuEl ||
          !this.isExpanded ||
          typeof ResizeObserver === "undefined"
        )
          return;
        this.resizeObserver = new ResizeObserver(this.scheduleReposition);
        this.resizeObserver.observe(menuEl);
      });
      if (this.refMenu) {
        this.refMenu.addEventListener("click", this.immediateClick);
      }
    },
    scheduleReposition() {
      if (this.repositionFrame) return;
      this.repositionFrame = requestAnimationFrame(() => {
        this.repositionFrame = null;
        if (this.isExpanded) {
          this.position();
        }
      });
    },
    immediateClick(event) {
      if (this.expanded) {
        if (this.closeOnClick) {
          event.stopImmediatePropagation();
          this.expanded = false;
        }
      }
    },
    globalClick(event) {
      if (this.triggerEvent === event) {
        return;
      }

      var parentElement = this.$refs.menu;
      var childElement = event.target || event.srcElement;

      // A click target that's been detached from the document between the
      // click and this (bubble-phase) handler is NOT an outside click. On a
      // real mouse click the browser runs a microtask checkpoint between the
      // target's own listener and ours, so a click that triggers a re-render
      // removing its own element (e.g. a composer whose send button swaps the
      // subtree on submit) leaves us with an orphaned node here. contains()
      // below would false-positive it as "outside" and collapse the menu, so
      // bail and keep the menu open.
      if (childElement && childElement.isConnected === false) {
        return;
      }

      if (parentElement && parentElement.contains(childElement)) {
        //Do nothing
        if (this.closeOnClick) {
          this.expanded = false;
          // event.stopImmediatePropagation();
        }
        return;
      }

      // Don't close when the click lands inside another floating overlay opened
      // from within this menu — a nested menu/dropdown (.ux-menu-outer) or a
      // modal (.modal-wrapper, e.g. a content picker's browse dialog). These are
      // teleported/rendered outside our panel but logically belong to it, so a
      // click in them must not collapse this menu. We match by container rather
      // than z-index: a menu grabs the maximum stacking index, so modals always
      // sit numerically *below* it and a z-index comparison can't detect them.
      //
      // But only skip for overlays that DON'T contain our own activator. An
      // overlay that contains our activator is the host we live inside (e.g. a
      // column picker opened from a table within a content-picker modal) —
      // clicking elsewhere in that host must still close us.
      const overlay =
        childElement.closest &&
        childElement.closest(".ux-menu-outer, .modal-wrapper");
      if (overlay && !overlay.contains(this.$el)) {
        return;
      }

      this.expanded = false;
    },
    position() {
      const rect = this.anchorRect || this.$el.getBoundingClientRect();

      // Initial positioning
      let left = rect.left + window.scrollX;
      let top = rect.bottom + window.scrollY;

      // Set initial position
      this.outerStyle.left = `${left}px`;
      this.outerStyle.top = `${top}px`;

      // Reset inner positioning
      this.innerStyle = {};

      // Apply right/bottom props if specified. When anchored to a cursor
      // point these are skipped so the menu expands down-right like a
      // normal context menu.
      if (this.right && !this.anchorRect) {
        this.innerStyle.right = `-${rect.width}px`;
      }

      if (this.bottom && !this.anchorRect) {
        this.innerStyle.bottom = `${rect.height}px`;
      }

      var zIndex = this.getMaxZIndex();
      this.outerStyle.zIndex = zIndex;

      // Wait for DOM to render the menu, then check boundaries
      this.$nextTick(() => {
        this.adjustForViewportBoundaries(rect);
      });
    },

    adjustForViewportBoundaries(triggerRect) {
      const menuEl = this.$refs.menu;
      if (!menuEl) return;

      // Measure the menu as positioned by position() — don't strip the inner
      // styles (right/bottom props), or right-aligned menus collapse to the
      // activator's left edge during the boundary check.
      const menuRect = menuEl.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      // Flip above the trigger if the menu doesn't fit below and there's more
      // room above. Anchor the panel's bottom edge to the activator's top edge
      // (same math as the `bottom` prop) so it stays visually attached instead
      // of floating wherever its height happened to land.
      const bottomOverflow = menuRect.bottom > viewport.height;
      const topSpace = triggerRect.top;
      const bottomSpace = viewport.height - triggerRect.bottom;

      if (bottomOverflow && topSpace > bottomSpace && !this.anchorRect) {
        this.innerStyle = {
          ...this.innerStyle,
          bottom: `${triggerRect.height}px`,
        };
        // Wait for the new bottom anchor to apply before clamping — otherwise
        // ensureWithinViewport measures the panel in its old (below-trigger)
        // position and shifts outer.top to compensate, opening a gap above.
        this.$nextTick(() => this.ensureWithinViewport());
      } else {
        this.ensureWithinViewport();
      }
    },

    ensureWithinViewport() {
      const menuEl = this.$refs.menu;
      if (!menuEl) return;

      const menuRect = menuEl.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      let currentLeft = parseInt(this.outerStyle.left) || 0;
      let currentTop = parseInt(this.outerStyle.top) || 0;

      // Ensure menu doesn't go beyond right edge
      if (menuRect.right > viewport.width) {
        currentLeft -= menuRect.right - viewport.width + 10; // 10px buffer
      }

      // Ensure menu doesn't go beyond left edge
      if (menuRect.left < 0) {
        currentLeft += Math.abs(menuRect.left) + 10; // 10px buffer
      }

      // Ensure menu doesn't go beyond bottom edge
      if (menuRect.bottom > viewport.height) {
        currentTop -= menuRect.bottom - viewport.height + 10; // 10px buffer
      }

      // Ensure menu doesn't go beyond top edge
      if (menuRect.top < 0) {
        currentTop += Math.abs(menuRect.top) + 10; // 10px buffer
      }

      // Apply corrected position
      this.outerStyle.left = `${Math.max(0, currentLeft)}px`;
      this.outerStyle.top = `${Math.max(0, currentTop)}px`;
    },
    getMaxZIndex() {
      return Math.max(
        ...Array.from(document.querySelectorAll("body *"), (el) =>
          parseFloat(window.getComputedStyle(el).zIndex),
        ).filter((zIndex) => !Number.isNaN(zIndex)),
        0,
      );
    },
    close() {
      this.expanded = false;
    },
    openAt(x, y) {
      // Anchor the menu to an arbitrary point (e.g. a cursor position)
      // instead of the activator element, so it can act as a context menu.
      this.anchorRect = {
        left: x,
        right: x,
        top: y,
        bottom: y,
        width: 0,
        height: 0,
      };

      if (this.isExpanded) {
        // Already open - just reposition to the new point.
        this.position();
      } else {
        this.expanded = true;
      }
    },
  },
  computed: {
    // isInViewport() {
    //     var element = this.$refs.menu;
    //     if(!element) {
    //         return false;
    //     }

    //     const rect = element.getBoundingClientRect();
    //     return (
    //         rect.top >= 0 &&
    //         rect.left >= 0 &&
    //         rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    //         rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    //     );
    // },
    expanded: {
      get() {
        return this.isExpanded;
      },
      set(v) {
        var previousActivatedEntity;

        if (this.group) {
          previousActivatedEntity = UXMenuService.cache[this.group];
        }

        ////////////////////////////////

        if (this.isExpanded != v) {
          this.isExpanded = v;

          //If we're collapsed
          if (v) {
            this.position();
            this.addEventListeners();

            UXMenuService.cache[this.group] = this;

            if (previousActivatedEntity && previousActivatedEntity != this) {
              previousActivatedEntity.expanded = false;
            }

            //////////////////////////////////////////
          } else {
            //Clean up the listeners
            this.removeListeners();

            //Drop any cursor anchor so the activator is used next time
            this.anchorRect = null;
          }
          this.$emit("update:modelValue", v);
        }
      },
    },
    on() {
      var self = this;

      return {
        click(event) {
          if (self.stop) {
            event.stopImmediatePropagation();
          }

          self.triggerEvent = event;
          self.expanded = !self.expanded;
        },
      };
    },
  },
};
</script>
<style lang="scss" scoped>
.ux-menu-outer {
  position: absolute;
  z-index: 10;
  font-size: 12px;
}

.ux-menu-panel {
  position: absolute;
  max-height: 70vh;
  max-width: calc(100vw - 20px);
  overflow: auto;
  &.flex {
    display: flex;
  }

  &.overflow {
    overflow: visible;
  }
  border: 1px solid color-mix(in lab, currentColor 10%, transparent);
  box-shadow: 0 0.2em 1em rgba(#000, 0.1);

  :deep(.ux-list-item) {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}
</style>
